import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { subscriptionService } from '../apis/subscriptionService';
import { useAuth } from './AuthContext';
import {
  isLimitExemptUser,
  isAccountLimitsLocked,
  isPremiumActive,
  isProPlusFeatureLocked,
  isStarterPlan,
  hasEarlyAccess,
  getPlanLoginReminder,
} from '../utils/accountLimitsUtils';
import PlanReminderModal from '../components/ui/plan-reminder-modal';
import { goToPricing } from '../constants/publicSite';

export { isLimitExemptUser, hasEarlyAccess };

const PLAN_REMINDER_SESSION_KEY = 'vbot_plan_login_reminder';
/** Set true temporarily to force popup on login for design preview. */
const FORCE_PLAN_REMINDER_PREVIEW = false;

const TrialContext = createContext();

export const useTrial = () => {
  const context = useContext(TrialContext);
  if (!context) {
    throw new Error('useTrial must be used within a TrialProvider');
  }
  return context;
};

export const TrialProvider = ({ children }) => {
  const { currentUser, loading: authLoading, refreshMe } = useAuth();
  const [trialStatus, setTrialStatus] = useState({
    hasTrialsLeft: false,
    remainingTrials: 0,
    totalTrials: 0,
    usedTrials: 0,
    planType: 'free',
    hasAccess: false,
    accessReason: 'UNKNOWN',
    canCreateChart: true,
    canUsePlatform: true,
    daysRemaining: null,
    subscriptionEnd: null,
    subscriptionStart: null,
    billingPeriod: null,
    billingPeriodLabel: null,
    planKey: null,
    seatCount: 1,
    isTrial: false,
    emrInputEligible: false,
    emrInputPriceCents: null,
    earlyAccess: false,
    priorityFeatureRequests: false,
    emrTurnaround: null,
    subscriptionDaysRemaining: null,
    chartsUsed: 0,
    chartsRemaining: null,
    maxCharts: 25,
    maxChartsPerDay: 25,
    maxChartsOverTrial: 175,
    trialDays: 7,
    isLoading: true,
    lastChecked: null
  });

  const [showTrialExhaustedModal, setShowTrialExhaustedModal] = useState(false);
  const [trialExhaustedReason, setTrialExhaustedReason] = useState(null);
  const [planReminder, setPlanReminder] = useState(null);
  const [showPlanReminderModal, setShowPlanReminderModal] = useState(false);
  const reminderEvalUserRef = useRef(null);
  const previewDismissedRef = useRef(false);

  // Check trial availability
  const checkTrialAvailability = async (forceRefresh = false) => {
    if (authLoading || !currentUser) {
      setTrialStatus(prev => ({ 
        ...prev, 
        isLoading: false,
        hasAccess: false,
        accessReason: 'NOT_AUTHENTICATED'
      }));
      return;
    }

    // Avoid redundant calls if we have recent data and not forcing refresh
    if (!forceRefresh && trialStatus.lastChecked && trialStatus.isLoading === false) {
      const timeSinceLastCheck = Date.now() - new Date(trialStatus.lastChecked).getTime();
      // Use cached data if it's less than 60 seconds old
      if (timeSinceLastCheck < 60000) {
        return;
      }
    }

    try {
      const trialInfo = await subscriptionService.checkTrialAvailability(forceRefresh);
      
      const newStatus = {
        ...trialInfo,
        planType: trialInfo.planType || 'free',
        hasAccess: trialInfo.hasAccess || false,
        accessReason: trialInfo.accessReason || 'UNKNOWN',
        isLoading: false,
        lastChecked: new Date()
      };
      
      setTrialStatus(newStatus);
    } catch (error) {
      const errorStatus = { 
        ...trialStatus, 
        isLoading: false,
        hasTrialsLeft: false,
        remainingTrials: 0,
        planType: 'free',
        hasAccess: false,
        accessReason: 'ERROR'
      };
      setTrialStatus(errorStatus);
    }
  };

  // Handle trial usage with automatic checks
  const handleTrialUsage = async (onSuccess, onFailure) => {
    if (authLoading || !currentUser) {
      if (onFailure) onFailure({ reason: 'NOT_AUTHENTICATED' });
      return false;
    }

    try {
      const result = await subscriptionService.useTrial();
      
      if (result.success) {
        const newTrialStatus = {
          ...trialStatus,
          remainingTrials: result.remainingTrials || 0,
          usedTrials: trialStatus.totalTrials - (result.remainingTrials || 0),
          hasTrialsLeft: (result.remainingTrials || 0) > 0,
          hasAccess: (result.remainingTrials || 0) > 0
        };
        
        setTrialStatus(newTrialStatus);
        if (onSuccess) {
          onSuccess(result);
        }
        return true;
      } else {
        setTrialExhaustedReason(result);
        setShowTrialExhaustedModal(true);
        if (onFailure) {
          onFailure(result);
        }
        return false;
      }
    } catch (error) {
      console.error('TrialContext: Error using trial:', error);
      const errorData = { reason: 'ERROR', error: error.message };
      
      if (onFailure) onFailure(errorData);
      return false;
    }
  };

  const isLimitExempt = useMemo(
    () => isLimitExemptUser(currentUser),
    [currentUser]
  );

  const isPremium = useMemo(
    () => isPremiumActive(trialStatus),
    [trialStatus]
  );

  const isAiFeatureLocked = useMemo(
    () =>
      isAccountLimitsLocked(trialStatus, {
        exempt: isLimitExempt,
      }),
    [trialStatus, isLimitExempt]
  );

  const isProPlusLocked = useMemo(
    () =>
      isProPlusFeatureLocked(trialStatus, {
        exempt: isLimitExempt,
      }),
    [trialStatus, isLimitExempt]
  );

  const isChartCreationLocked = useMemo(() => {
    if (isLimitExempt || trialStatus.isLoading) {
      return false;
    }
    // Soft Pro trial keeps daily chart cap — respect canCreateChart.
    if (trialStatus.isTrial) {
      return trialStatus.canCreateChart === false;
    }
    // Paid Pro/Elite: always unlocked. Starter/free: New Visit locks on
    // trial expiry / chart cap (Upload/Capture stay unlocked, see isAiFeatureLocked).
    if (isPremium && !isStarterPlan(trialStatus)) {
      return false;
    }
    return trialStatus.canCreateChart === false;
  }, [trialStatus, isLimitExempt, isPremium]);

  const getAccountLimitMessage = useCallback(() => {
    const reason =
      trialStatus.accessReason ||
      trialStatus.accountLimits?.restrictionReason;
    const maxPerDay =
      trialStatus.maxChartsPerDay || trialStatus.maxCharts || 25;
    const maxPerMonth =
      trialStatus.maxChartsPerMonth ||
      trialStatus.accountLimits?.chartLimit?.maxChartsPerMonth ||
      50;
    const trialDays = trialStatus.trialDays || 7;

    if (reason === "MONTHLY_NOTE_LIMIT_REACHED") {
      return `Starter includes ${maxPerMonth} AI SOAP notes per month. Upgrade to Professional for unlimited notes.`;
    }
    if (reason === "NOTE_HISTORY_EXPIRED") {
      return `Starter includes 30-day note history. Upgrade to Professional for full history.`;
    }
    if (reason === "STARTER_FEATURE_LOCKED") {
      return `Magic Editor and HPI modes are available on Professional and Elite. Upgrade to unlock.`;
    }
    if (reason === "CHART_LIMIT_REACHED") {
      return `You have reached today's limit of ${maxPerDay} charts. Please upgrade to continue.`;
    }
    if (reason === "TRIAL_PERIOD_EXPIRED") {
      return `Your ${trialDays}-day trial has ended. Please upgrade to continue.`;
    }
    if (trialStatus.canCreateChart === false || trialStatus.canUsePlatform === false) {
      return `Your free access is limited. Please upgrade to continue.`;
    }
    return `Please upgrade to continue.`;
  }, [trialStatus]);

  // Check if user can access features (use cached trial status)
  const canAccessFeature = async () => {
    if (authLoading || !currentUser) {
      return false;
    }

    if (isLimitExempt || isPremium) {
      return true;
    }

    const hasAccountAccess =
      trialStatus.canUsePlatform !== false;

    // If we already have recent trial status, use it instead of making another API call
    if (trialStatus.lastChecked && trialStatus.hasAccess !== undefined) {
      const timeSinceLastCheck = Date.now() - new Date(trialStatus.lastChecked).getTime();
      // Use cached data if it's less than 30 seconds old
      if (timeSinceLastCheck < 30000) {
        return hasAccountAccess;
      }
    }

    try {
      const accessInfo = await subscriptionService.checkPlatformAccess();
      const hasAccess = accessInfo.hasAccess || false;
      const canUsePlatform = accessInfo.data?.canUsePlatform !== false;
      const canCreateChart = accessInfo.data?.canCreateChart !== false;
      
      // Update trial status with access info
      if (accessInfo.data) {
        setTrialStatus(prev => ({
          ...prev,
          hasAccess: hasAccess && canUsePlatform,
          accessReason:
            accessInfo.data.accountLimits?.restrictionReason ||
            accessInfo.data.reason ||
            'UNKNOWN',
          canUsePlatform,
          canCreateChart,
          accountLimits: accessInfo.data.accountLimits || prev.accountLimits,
          lastChecked: new Date()
        }));
      }
      
      return hasAccess && canUsePlatform;
    } catch (error) {
      console.error('TrialContext: Error checking platform access:', error);
      return false;
    }
  };

  // Refresh trial status
  const refreshTrialStatus = useCallback(() => {
    subscriptionService.clearSubscriptionCache();
    setTrialStatus((prev) => ({ ...prev, isLoading: true }));
    checkTrialAvailability(true);
  }, [currentUser, authLoading]);

  // Close trial exhausted modal
  const closeTrialExhaustedModal = useCallback(() => {
    setShowTrialExhaustedModal(false);
    setTrialExhaustedReason(null);
  }, []);

  const closePlanReminderModal = useCallback(() => {
    setShowPlanReminderModal(false);
    if (FORCE_PLAN_REMINDER_PREVIEW) {
      previewDismissedRef.current = true;
    }
  }, []);

  // Navigate to payment page
  const navigateToPayment = () => {
    closeTrialExhaustedModal();
    closePlanReminderModal();
    window.location.href = '/payment';
  };

  // "Compare plans" / "View pricing" — the pricing page lives on the public
  // marketing site (different domain), not in this app. Open it in a new tab
  // and leave the modal in place so the user can still pick "Choose a plan".
  const navigateToPricing = () => {
    closeTrialExhaustedModal();
    closePlanReminderModal();
    goToPricing();
  };

  // Once per login session: remind if plan exhausted or ≤3 days left
  useEffect(() => {
    if (authLoading || !currentUser || trialStatus.isLoading) return;

    const userId = currentUser.userId || currentUser.id || currentUser.email;
    if (!userId) return;

    if (reminderEvalUserRef.current !== userId) {
      reminderEvalUserRef.current = userId;
      previewDismissedRef.current = false;
    }

    if (FORCE_PLAN_REMINDER_PREVIEW && previewDismissedRef.current) {
      return;
    }

    if (!FORCE_PLAN_REMINDER_PREVIEW) {
      let alreadyShown = false;
      try {
        alreadyShown =
          sessionStorage.getItem(PLAN_REMINDER_SESSION_KEY) === String(userId);
      } catch {
        alreadyShown = false;
      }
      if (alreadyShown) return;
    }

    const reminder =
      getPlanLoginReminder(trialStatus, {
        exempt: FORCE_PLAN_REMINDER_PREVIEW ? false : isLimitExempt,
      }) ||
      (FORCE_PLAN_REMINDER_PREVIEW
        ? {
            variant: "expiring_soon",
            reason: "TRIAL_ENDING_SOON",
            daysRemaining: 3,
            source: "trial",
          }
        : null);
    if (!reminder) return;

    if (!FORCE_PLAN_REMINDER_PREVIEW) {
      try {
        sessionStorage.setItem(PLAN_REMINDER_SESSION_KEY, String(userId));
      } catch {
        /* ignore */
      }
    }

    setPlanReminder(reminder);
    setShowPlanReminderModal(true);
  }, [authLoading, currentUser, trialStatus, isLimitExempt]);

  // Clear reminder session when logged out
  useEffect(() => {
    if (!authLoading && !currentUser) {
      reminderEvalUserRef.current = null;
      previewDismissedRef.current = false;
      setShowPlanReminderModal(false);
      setPlanReminder(null);
      try {
        sessionStorage.removeItem(PLAN_REMINDER_SESSION_KEY);
      } catch {
        /* ignore */
      }
    }
  }, [authLoading, currentUser]);

  // Refresh limits when user returns to the tab (e.g. after midnight chart reset)
  useEffect(() => {
    if (!currentUser) return undefined;
    const onFocus = () => checkTrialAvailability(true);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [currentUser]);

  // Check trial status on mount and when user changes
  useEffect(() => {
    if (!authLoading && currentUser) {
      checkTrialAvailability();
    } else if (!authLoading && !currentUser) {
      const resetStatus = {
        hasTrialsLeft: false,
        remainingTrials: 0,
        totalTrials: 0,
        usedTrials: 0,
        planType: 'free',
        hasAccess: false,
        accessReason: 'NOT_AUTHENTICATED',
        canCreateChart: false,
        canUsePlatform: false,
        daysRemaining: null,
        chartsUsed: 0,
        chartsRemaining: null,
        maxCharts: 25,
        maxChartsPerDay: 25,
        maxChartsOverTrial: 175,
        trialDays: 7,
        isLoading: false,
        lastChecked: null
      };
      setTrialStatus(resetStatus);
    }
  }, [authLoading, currentUser]);

  const value = {
    trialStatus,
    isLimitExempt,
    isAiFeatureLocked,
    isProPlusLocked,
    isChartCreationLocked,
    getAccountLimitMessage,
    showTrialExhaustedModal,
    trialExhaustedReason,
    showPlanReminderModal,
    planReminder,
    checkTrialAvailability,
    handleTrialUsage,
    canAccessFeature,
    refreshTrialStatus,
    closeTrialExhaustedModal,
    closePlanReminderModal,
    navigateToPayment,
    navigateToPricing,
    hasEarlyAccess: hasEarlyAccess(trialStatus, { exempt: isLimitExempt }),
  };

  return (
    <TrialContext.Provider value={value}>
      {children}
      <PlanReminderModal
        isOpen={showPlanReminderModal}
        onClose={closePlanReminderModal}
        onSubscribe={navigateToPayment}
        onViewPricing={navigateToPricing}
        reminder={planReminder}
        currentUser={currentUser}
        refreshMe={refreshMe}
      />
    </TrialContext.Provider>
  );
};